import type { MappedPublicReport } from '@/api/mappers/public-report.mapper';
import type { AvailableSection } from '@/api/mappers/public-report.mapper';
import type { AccordionItem, SubItem } from '@/components/SidebarAccordion/SidebarAccordion';

type TranslationFunction = (key: string, defaultValue?: string) => string;

/**
 * Buduje listę elementów menu na podstawie dostępnych sekcji w raporcie
 */
const buildMenuItems = (
  reportData: MappedPublicReport,
  t?: TranslationFunction
): AccordionItem[] => {
  const items: AccordionItem[] = [];
  const has = (s: AvailableSection) => reportData.availableSections.has(s);

  if (has('REPORT_SUMMARY') && reportData.summary) {
    items.push({
      label: t ? t('menu.summary', 'Podsumowanie') : 'Skrót raportu',
      value: 'skrot-raportu',
      elementId: 'skrot-raportu',
      icon: 'paper',
    });
  }

  if (has('CAR_BASIC_INFO')) {
    items.push({
      label: t
        ? t('menu.basic_info', 'Podstawowe informacje')
        : 'Podstawowe informacje',
      value: 'podstawowe-informacje',
      elementId: 'podstawowe-informacje',
      icon: 'i',
    });
  }

  if (has('EXTERNAL_INSPECTION') && reportData.carBody) {
    const carBodySubItems = [
      {
        label: "Podsumowanie",
        elementId: "podsumowanie-ekspertyzy-nadwozia"
      },
      {
        label: "Pomiary szczelin",
        elementId: "pomiary-szczelin"
      }, {
        label: "Pomiary lakieru",
        elementId: "pomiary-lakieru"
      }, {
        label: "Pomiary lakieru i wnęk",
        elementId: "pomiary-lakieru-i-wnek"
      }, {
        label: "Kontrola szyb",
        elementId: "kontrola-szyb"
      }, {
        label: "Kontrola ogumienia",
        elementId: "kontrola-ogumienia"
      }, {
        label: "Kontrola oświetlenia",
        elementId: "kontrola-oswietlenia"
      }
    ];

    if (reportData.galleries.body.length > 0) {
      carBodySubItems.push({
        label: "Galeria",
        elementId: "galeria-nadwozia"
      });
    }

    items.push({
      label: t ? t('menu.car_body', 'Nadwozie') : 'Nadwozie',
      value: 'car-body',
      elementId: 'car-body',
      icon: 'car',
      items: carBodySubItems
    });
  }

  if (has('INTERNAL_INSPECTION') && reportData.carInterior) {
    const carInteriorSubItems = [
      {
        label: "Podsumowanie",
        elementId: "podsumowanie-ekspertyzy-wnetrza"
      },
      {
        label: "Stan elementów kokpitu",
        elementId: "stan-elementow-kokpitu"
      },
      {
        label: "Stan elementów wnętrza",
        elementId: "stan-elementow-wnetrza"
      },
      {
        label: "Wyposażenie",
        elementId: "wyposazenie-wnetrze"
      }
    ];

    if (reportData.galleries.interior.length > 0) {
      carInteriorSubItems.push({
        label: "Galeria",
        elementId: "galeria-wnetrza"
      });
    }

    items.push({
      label: t ? t('menu.car_interior', 'Wnętrze') : 'Wnętrze',
      value: 'car-interior',
      elementId: 'car-interior',
      icon: 'trophy',
      items: carInteriorSubItems
    });
  }

  if (has('ENGINE_AND_TRUNK_COMPARTMENT') && reportData.engineAndTrunk) {
    const engineTrunkSubItems = [
      {
        label: "Podsumowanie",
        elementId: "podsumowanie-ekspertyzy-komory"
      },
      {
        label: "Stan komory silnika",
        elementId: "stan-komory-silnika"
      },
      {
        label: "Stan komory bagażnika",
        elementId: "stan-komory-bagaznika"
      }
    ];

    if (reportData.galleries.engineCompartment.length > 0) {
      engineTrunkSubItems.push({
        label: "Galeria",
        elementId: "galeria-komory"
      });
    }

    items.push({
      label: t
        ? t('menu.engine_trunk', 'Komora silnika i bagażnika')
        : 'Komora silnika i bagażnika',
      value: 'engine-trunk',
      elementId: 'engine-trunk',
      icon: 'handshake',
      items: engineTrunkSubItems
    });
  }

  if (has('TEST_DRIVE') && reportData.testDrive) {
    items.push({
      label: t ? t('menu.test_drive', 'Jazda próbna') : 'Jazda próbna',
      value: 'test-drive',
      elementId: 'test-drive',
      icon: 'steering',
      items: [
        {
          label: "Podsumowanie",
          elementId: "podsumowanie-jazdy-probnej"
        },
        {
          label: "Szczegółowy raport",
          elementId: "szczegolowy-raport-jazdy-probnej"
        }
      ]
    });
  }

  if (has('CAR_CHASSIS') && reportData.chassis) {
    const chassisSubItems = [
      {
        label: "Podsumowanie",
        elementId: "podsumowanie-kontroli-podwozia"
      },
      {
        label: "Stan podwozia",
        elementId: "stan-podwozia"
      }
    ];

    if (reportData.galleries.undercarriage.length > 0) {
      chassisSubItems.push({
        label: "Galeria",
        elementId: "galeria-podwozia"
      });
    }

    items.push({
      label: t ? t('menu.chassis', 'Podwozie') : 'Podwozie',
      value: 'chassis',
      elementId: 'chassis',
      icon: 'network-nodes',
      items: chassisSubItems
    });
  }

  if (
    has('COMPUTER_DIAGNOSTICS') &&
    reportData.computerDiagnostics) {
    const diagnosticsSubItems = [
      {
        label: "Podsumowanie",
        elementId: "podsumowanie-diagnostyki-komputerowej"
      }
    ];

    if (reportData.galleries.diagnostics.length > 0) {
      diagnosticsSubItems.push({
        label: "Galeria",
        elementId: "galeria-diagnostyki"
      });
    }

    items.push({
      label: t
        ? t('menu.computer_diagnostics', 'Komputer diagnostyczny')
        : 'Komputer diagnostyczny',
      value: 'computer-diagnostics',
      elementId: 'computer-diagnostics',
      icon: 'monitor-screen',
      items: diagnosticsSubItems
    });
  }

  if (has('REPAIR_HISTORY') && reportData.repairHistory) {
    const repairHistorySubItems: { label: string; elementId: string }[] = [
      {
        label: "Podsumowanie",
        elementId: "historia-napraw"
      }
    ];

    if (reportData.galleries.documentation.length > 0) {
      repairHistorySubItems.push({
        label: "Galeria",
        elementId: "galeria-historii-napraw"
      });
    }

    items.push({
      label: t ? t('menu.repair_history', 'Historia napraw') : 'Historia napraw',
      value: 'historia-napraw',
      elementId: 'historia-napraw',
      icon: 'wrench-tool',
      items: repairHistorySubItems,
    });
  }

  if (reportData.cepikSection) {
    items.push({
      label: t ? t('menu.cepik', 'CEPiK') : 'Informacje o pojeździe',
      value: 'informacje-o-pojezdzie',
      elementId: 'informacje-o-pojezdzie',
      icon: 'copyright',
      items: [
        {
          label: 'Dane techniczne',
          elementId: 'informacje-o-pojezdzie-dane-techniczne',
        },
        {
          label: 'Dokumenty',
          elementId: 'informacje-o-pojezdzie-dokumenty',
        },
        {
          label: 'Podsumowanie zdarzeń',
          elementId: 'informacje-o-pojezdzie-podsumowanie-zdarzen',
        },
        {
          label: 'Stan aktualny',
          elementId: 'informacje-o-pojezdzie-stan-aktualny',
        }
      ],
    });
  }

  const hasGeneralGallery =
    reportData.galleries.all.length > 0 ||
    reportData.galleries.body.length > 0 ||
    reportData.galleries.interior.length > 0 ||
    reportData.galleries.engineCompartment.length > 0;

  if (hasGeneralGallery) {
    items.push({
      label: t ? t('menu.gallery', 'Galeria zdjęć') : 'Galeria zdjęć',
      value: 'gallery',
      elementId: 'gallery',
      icon: 'grid-squares',
    });
  }

  return items;
};

/**
 * Buduje listę elementów dla sekcji "Dodatkowe usługi"
 * (video z oględzin + raporty dodatkowe / addony)
 */
const buildAdditionalServicesItems = (
  reportData: MappedPublicReport,
  t?: TranslationFunction
): AccordionItem[] => {
  const items: AccordionItem[] = [];

  if (reportData.videoUrl) {
    items.push({
      label: t ? t('menu.video', 'Film z oględzin') : 'Film z oględzin',
      value: 'video',
      elementId: 'video',
      icon: 'video-camera',
    });
  }

  if (reportData.addons.length > 0) {
    const addonSubItems: SubItem[] = reportData.addons.map((addon) => ({
      label: addon.typeDisplayName || addon.type,
      elementId: `dodatkowe-uslugi-${addon.id}`,
    }));

    items.push({
      label: t ? t('menu.addons', 'Dodatkowe usługi') : 'Dodatkowe usługi',
      value: 'dodatkowe-uslugi',
      elementId: 'dodatkowe-uslugi',
      icon: 'paper',
      items: addonSubItems,
    });
  }

  return items;
};

/**
 * Generuje elementy menu na podstawie dostępnych sekcji w raporcie
 * Wyświetla tylko te sekcje, które faktycznie zawierają dane
 */
export const generateReportMenuItems = (
  reportData: MappedPublicReport | null,
  t?: TranslationFunction
): AccordionItem[] => {
  if (!reportData) {
    return [];
  }

  return buildMenuItems(reportData, t);
};

/**
 * Generuje elementy menu dla sekcji "Dodatkowe usługi"
 */
export const generateAdditionalServicesItems = (
  reportData: MappedPublicReport | null,
  t?: TranslationFunction
): AccordionItem[] => {
  if (!reportData) {
    return [];
  }

  return buildAdditionalServicesItems(reportData, t);
};
